import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import QRCode from 'qrcode.react';
import Barcode from 'react-barcode';
import JsBarcode from 'jsbarcode';
import { mmToPx, getPageDimsPx, getInitialPreviewLang, getOrderedTemplateLanguages, getPreviewText, getShadowCss, getDropShadowCss, getVerticalAlignCss, isClipShape, SHAPE_CLIP_PATHS, normalizeFreeText, buildPrintHtml, SAMPLE_BINDINGS } from './templateUtils';

const PREVIEW_ZOOM = 0.75;
const MM_TO_PX_96DPI = 96 / 25.4;

class TemplateDesignerPreviewModal extends Component {
    constructor(props) {
        super(props);
        this.state = { previewLang: getInitialPreviewLang() };
    }

    _renderElementContent = (el) => {
        // Sizes in pre-transform space (canvas div applies transform:scale)
        const w = mmToPx(el.w, 1);
        const h = mmToPx(el.h, 1);
        const { previewLang } = this.state;
        const value = el.binding ? (SAMPLE_BINDINGS[el.binding] || el.binding) : getPreviewText(el.freeText, previewLang);

        switch (el.type) {
            case 'text':
                return (
                    <div
                        style={{
                            width: '100%',
                            height: '100%',
                            ...(getVerticalAlignCss(el.verticalAlign) ? { display: 'flex', flexDirection: 'column', justifyContent: getVerticalAlignCss(el.verticalAlign) } : {}),
                            fontFamily: el.fontFamily || 'Arial',
                            fontSize: el.fontSize || 14,
                            fontWeight: el.bold ? 'bold' : 'normal',
                            fontStyle: el.italic ? 'italic' : 'normal',
                            textDecoration: el.underline ? 'underline' : 'none',
                            color: el.colour || '#333333',
                            textAlign: el.textAlign || 'left',
                            backgroundColor: el.bgColour || 'transparent',
                            border: (el.borderWidth || 0) > 0
                                ? `${el.borderWidth}px solid ${el.borderColour || '#000000'}`
                                : 'none',
                            borderRadius: `${el.borderRadius || 0}px`,
                            padding: `${el.padding ?? 4}px`,
                            boxSizing: 'border-box',
                            overflow: 'hidden',
                            lineHeight: el.lineHeight || 1.2,
                            letterSpacing: `${el.letterSpacing || 0}px`,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                        }}
                    >
                        {value || ' '}
                    </div>
                );

            case 'image':
                return el.src ? (
                    <img src={el.src} alt="" style={{ width: '100%', height: '100%', objectFit: el.fit || 'contain', borderRadius: `${el.borderRadius || 0}px` }} />
                ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed #adb5bd', color: '#adb5bd', fontSize: '0.7rem' }}>
                        Image
                    </div>
                );

            case 'qr': {
                const qrValue = value || 'QR';
                const qrSize = Math.max(20, Math.min(w, h) - 4);
                return (
                    <div data-qr-id={el.id} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <QRCode value={qrValue} size={qrSize} fgColor={el.qrColour || '#000000'} bgColor={el.qrBgColour || '#ffffff'} />
                    </div>
                );
            }

            case 'barcode': {
                const bcValue = value || '123456789';
                return (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                        <Barcode value={bcValue} width={1} height={Math.max(20, h - 10)} fontSize={10} margin={0} />
                    </div>
                );
            }

            case 'shape':
                if (el.shapeType === 'line') {
                    return (
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}>
                            <div style={{ width: '100%', height: el.borderWidth ?? 1, backgroundColor: el.borderColour || '#000000' }} />
                        </div>
                    );
                }
                if (el.shapeType === 'ellipse') {
                    return (
                        <div style={{ width: '100%', height: '100%', backgroundColor: el.fillColour || '#ffffff', border: (el.borderWidth ?? 1) > 0 ? `${el.borderWidth ?? 1}px solid ${el.borderColour || '#000000'}` : 'none', borderRadius: '50%', boxSizing: 'border-box' }} />
                    );
                }
                if (SHAPE_CLIP_PATHS[el.shapeType]) {
                    return (
                        <div style={{ width: '100%', height: '100%', filter: getDropShadowCss(el.shadow) || 'none' }}>
                            <div style={{ width: '100%', height: '100%', backgroundColor: el.fillColour || '#ffffff', clipPath: SHAPE_CLIP_PATHS[el.shapeType] }} />
                        </div>
                    );
                }
                return (
                    <div style={{ width: '100%', height: '100%', backgroundColor: el.fillColour || '#ffffff', border: (el.borderWidth ?? 1) > 0 ? `${el.borderWidth ?? 1}px solid ${el.borderColour || '#000000'}` : 'none', borderRadius: `${el.borderRadius || 0}px`, boxSizing: 'border-box' }} />
                );

            default:
                return null;
        }
    };

    _handlePrint = () => {
        const { template } = this.props;
        const { previewLang } = this.state;
        const elements = template.elements || [];

        // Pre-render barcodes with the same params as the canvas so print matches
        const barcodeDataUrls = {};
        elements.filter(el => el.type === 'barcode').forEach(el => {
            const bcVal = el.binding ? (SAMPLE_BINDINGS[el.binding] || el.binding) : getPreviewText(el.freeText, previewLang) || '123456789';
            const canvas = document.createElement('canvas');
            try {
                JsBarcode(canvas, bcVal || '123456789', {
                    format: 'CODE128',
                    width: 1,
                    height: Math.max(20, Math.round(el.h * MM_TO_PX_96DPI) - 10),
                    fontSize: 10,
                    margin: 0,
                    displayValue: true,
                });
                barcodeDataUrls[el.id] = canvas.toDataURL('image/png');
            } catch (e) {
                barcodeDataUrls[el.id] = null;
            }
        });

        // Reuse the rendered QR canvases so print shows the real code, not a placeholder
        elements.filter(el => el.type === 'qr').forEach(el => {
            const qrCanvas = document.querySelector(`[data-qr-id="${el.id}"] canvas`);
            if (qrCanvas) {
                try {
                    barcodeDataUrls[el.id] = qrCanvas.toDataURL('image/png');
                } catch (e) {
                    barcodeDataUrls[el.id] = null;
                }
            }
        });

        const html = buildPrintHtml(template, elements, barcodeDataUrls, previewLang);
        const win = window.open('', '_blank');
        if (!win) return;
        win.document.write(html);
        win.document.close();

        // Wait for every <img> AND the CSS bg image before printing (immediate print raced
        // the downloads and the PDF could miss them); 5s cap so a broken URL never blocks.
        const whenImagesReady = () => {
            const imgPromises = Array.from(win.document.images).map(img =>
                (img.complete
                    ? Promise.resolve()
                    : new Promise(resolve => { img.onload = resolve; img.onerror = resolve; }))
            );

            if (template.bgImage) {
                imgPromises.push(new Promise(resolve => {
                    const probe = new win.Image();
                    probe.onload = resolve;
                    probe.onerror = resolve;
                    probe.src = template.bgImage;
                }));
            }

            const timeout = new Promise(resolve => setTimeout(resolve, 5000));

            Promise.race([Promise.all(imgPromises), timeout]).then(() => {
                win.focus();
                // print() blocks this window's script until the dialog closes,
                // so the helper window can be closed right after
                win.print();
                win.close();
            });
        };

        if (win.document.readyState === 'complete') {
            whenImagesReady();
        } else {
            win.onload = whenImagesReady;
        }
    };

    render() {
        const { isOpen, toggle, template } = this.props;
        const { previewLang } = this.state;
        const elements = template.elements || [];
        const dimsPx = getPageDimsPx(template.pageSize, template.orientation);

        const canvasW = dimsPx.w;
        const canvasH = dimsPx.h;
        const containerW = Math.round(canvasW * PREVIEW_ZOOM);
        const containerH = Math.round(canvasH * PREVIEW_ZOOM);

        return (
            <Modal isOpen={isOpen} toggle={toggle} fade={false} size="xl" style={{ maxWidth: Math.min(containerW + 60, window.innerWidth - 40) }}>
                <ModalHeader toggle={toggle}>
                    <FormattedMessage id="TemplateDesigner.Toolbar.Preview" /> — {template.name || 'Untitled'}
                </ModalHeader>
                <ModalBody style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '80vh', display: 'flex', justifyContent: 'center', padding: 16 }}>
                    <div style={{ width: containerW, height: containerH, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.25)', flexShrink: 0 }}>
                        <div
                            style={{
                                position: 'relative',
                                width: canvasW,
                                height: canvasH,
                                backgroundColor: template.bgColour || '#ffffff',
                                transform: `scale(${PREVIEW_ZOOM})`,
                                transformOrigin: '0 0',
                            }}
                        >
                            {/* Background image — own layer so it can carry opacity */}
                            {template.bgImage && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: 0, left: 0, right: 0, bottom: 0,
                                        backgroundImage: `url(${template.bgImage})`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        opacity: (template.bgImageOpacity ?? 100) / 100,
                                        zIndex: 0,
                                        pointerEvents: 'none',
                                    }}
                                />
                            )}
                            {/* Margin guide — pre-transform coords, canvas scale handles zoom */}
                            {(template.pageMarginX > 0 || template.pageMarginY > 0) && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: mmToPx(template.pageMarginY || 0, 1),
                                        left: mmToPx(template.pageMarginX || 0, 1),
                                        right: mmToPx(template.pageMarginX || 0, 1),
                                        bottom: mmToPx(template.pageMarginY || 0, 1),
                                        border: '1px dashed rgba(0,123,255,0.4)',
                                        pointerEvents: 'none',
                                        zIndex: 9999,
                                    }}
                                />
                            )}

                            {elements.map(el => {
                                // Pre-transform coords — canvas transform:scale handles zoom
                                const x = mmToPx(el.x, 1);
                                const y = mmToPx(el.y, 1);
                                const w = mmToPx(el.w, 1);
                                const h = mmToPx(el.h, 1);
                                const m = mmToPx(el.margin || 0, 1);

                                return (
                                    <div
                                        key={el.id}
                                        style={{
                                            position: 'absolute',
                                            left: x,
                                            top: y,
                                            width: w,
                                            height: h,
                                            zIndex: el.zIndex || 1,
                                            overflow: 'hidden',
                                            boxSizing: 'border-box',
                                            boxShadow: (!isClipShape(el) && getShadowCss(el.shadow)) || 'none',
                                            borderRadius: `${el.borderRadius || 0}px`,
                                            transform: (el.rotation || 0) !== 0 ? `rotate(${el.rotation}deg)` : 'none',
                                        }}
                                    >
                                        <div style={{ position: 'absolute', top: m, left: m, right: m, bottom: m, overflow: 'hidden', opacity: (el.opacity ?? 100) / 100 }}>
                                            {this._renderElementContent(el)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    {/* Language selector */}
                    <div style={{ display: 'flex', gap: 3, marginRight: 'auto' }}>
                        {getOrderedTemplateLanguages().map(({ code, flagClass, label }) => (
                            <button
                                key={code}
                                type="button"
                                title={label}
                                onClick={() => this.setState({ previewLang: code })}
                                style={{
                                    padding: '3px 6px',
                                    border: '1px solid #ced4da',
                                    borderRadius: 3,
                                    backgroundColor: previewLang === code ? '#e8f0fe' : '#fff',
                                    outline: previewLang === code ? '2px solid #007bff' : 'none',
                                    cursor: 'pointer',
                                    opacity: previewLang === code ? 1 : 0.5,
                                    lineHeight: 1,
                                }}
                            >
                                <span className={flagClass} style={{ fontSize: '1.1rem' }} />
                            </button>
                        ))}
                    </div>
                    <Button color="primary" onClick={this._handlePrint}>
                        <i className="fa-solid fa-print mr-1" />
                        <FormattedMessage id="TemplateDesigner.Preview.Print" />
                    </Button>
                    <Button color="secondary" onClick={toggle}>
                        <FormattedMessage id="General.Close" />
                    </Button>
                </ModalFooter>
            </Modal>
        );
    }
}

export default TemplateDesignerPreviewModal;
